import { SankeyData, SankeyDataLink, SankeyDataNode } from 'types/sankey';

import { PathLink } from '@/components/sanky/sankey-link';
import { RectNode } from '@/components/sanky/sankey-rect';

const MAX_CHILD_ALLOWED = 5;
const NODES_DEFAULT_OPACITY = 1;
const LINKS_DEFAULT_OPACITY = 0.4;
export const NODES_HIGHLIGHTED_OPACITY = 1;
const LINKS_HIGHLIGHTED_OPACITY = 0.56;
export const NODES_DIMMED_OPACITY = 0.24;
const LINKS_DIMMED_OPACITY = 0.12;

export function findRootNode(nodes: { [key: string]: any }): string | null {
  /**
   * Find the root node in the given JSON object.
   *
   * The root node is the one that has no dots in its key.
   *
   * @param nodes The JSON object to search in.
   * @returns The key of the root node, or null if no root node is found.
   */
  for (const key in nodes) {
    if (!key.includes('.')) {
      return key;
    }
  }

  return null;
}
// filter and return only the visible nodes and links
// hide nodes with 0 value, except Quality view
function filterVisibleNodesAndLinks(
  nodes: SankeyDataNode[],
  links: SankeyDataLink[],
  selectedView?: string
): SankeyData {
  const hideZeroValueNodes =
    selectedView !== 'Quality' && selectedView !== 'Replay Tests';
  const visibleNodes = nodes.filter(
    (node) => node.isVisible && (!hideZeroValueNodes || node.value !== 0)
  );
  // create a set of visible node keys
  const visibleNodeKeys = new Set(visibleNodes.map((node) => node.key));
  const visibleLinks = links.filter(
    (link) =>
      link.isVisible &&
      visibleNodeKeys.has(link.source.key) &&
      visibleNodeKeys.has(link.target.key)
  );

  return { nodes: visibleNodes, links: visibleLinks };
}

// this function is called in two sceanrios:
// - initial render: to mark first 2 levels as visible (root and its children)
// - when a node is clicked.: to mark its children and links as visible
export function getDefaultNodesOrExpand(
  rootNodeKey: string,
  sankeyData: SankeyData,
  selectedView?: string,
  isInitialRender?: boolean
): SankeyData {
  const { nodes, links } = sankeyData;

  // Mark root node as visible: for initial render only.
  const rootNode = nodes.find((node) => node.key === rootNodeKey);
  if (!rootNode) {
    return { nodes: [], links: [] };
  }
  rootNode.isVisible = true;

  if (selectedView && isInitialRender) {
    expandNodesByView(nodes, links, selectedView, rootNodeKey);
  } else {
    expandDefaultLevel(rootNodeKey, nodes, links, selectedView);
  }

  return filterVisibleNodesAndLinks(nodes, links, selectedView);
}

export function getNodeDepth(
  node: SankeyDataNode,
  rootNodeKey: string,
  nodes: SankeyDataNode[]
): number {
  if (node.key === rootNodeKey) return 0;

  let depth = 0;
  let currentNode = node;

  // Count how many parent steps to reach the root
  while (currentNode.parentKey && currentNode.parentKey !== null) {
    depth++;
    const parentNode = nodes.find((n) => n.key === currentNode.parentKey);
    if (!parentNode) break;

    if (parentNode.key === rootNodeKey) {
      break;
    }

    currentNode = parentNode;
  }

  return depth;
}
// Expand all nodes that support the selected view
function expandNodesByView(
  nodes: SankeyDataNode[],
  links: SankeyDataLink[],
  selectedView: string,
  rootNodeKey: string
) {
  // Show ALL nodes, but mark unsupported ones as disabled
  nodes.forEach((node) => {
    const isRoot = node.key === rootNodeKey;
    // keep node visible if it has view-supporting descendant
    const hasDescendantSupportingView = nodes.some(
      (n) =>
        n.key.startsWith(node.key + '.') && nodeSupportsView(n, selectedView)
    );

    const supportsView =
      isRoot ||
      nodeSupportsView(node, selectedView) ||
      hasDescendantSupportingView;

    if (supportsView) {
      node.isVisible = true;
      node.opacity = NODES_DEFAULT_OPACITY;
      node.isDisabled = false;
    } else if (selectedView === 'Quality' || selectedView === 'Replay Tests') {
      // for Quality view, show all nodes, but dim unsupported nodes without quality relevant descendants
      node.isVisible = true;
      node.opacity = NODES_DIMMED_OPACITY;
      node.isDisabled = true;
    }
  });

  // Limit children to MAX_CHILD_ALLOWED for each visible parent
  nodes.forEach((parentNode) => {
    if (!parentNode.isVisible) return;

    const children = nodes.filter(
      (node) => node.parentKey === parentNode.key && node.isVisible
    );
    // Separate "Other" node (for dynamic children) from individual children
    const otherNode = children.find((child) => child.name === 'Other');
    const individualChildren = children.filter(
      (child) => child.name !== 'Other'
    );

    // Quality view children are a fixed set of eval labels - never hide any
    if (
      children.length > MAX_CHILD_ALLOWED &&
      selectedView !== 'Quality' &&
      selectedView !== 'Replay Tests'
    ) {
      // Hide excess individual children beyond the limit
      individualChildren.slice(MAX_CHILD_ALLOWED).forEach((child) => {
        child.isVisible = false;
      });

      // Always include "Other" node if it exists
      if (otherNode) {
        otherNode.isVisible = true;
      }
    }
  });

  // Show ALL links, but dim unsupported ones
  links.forEach((link) => {
    link.isVisible = true;

    const sourceNode = nodes.find((node) => node.key === link.source.key);
    const targetNode = nodes.find((node) => node.key === link.target.key);

    if (sourceNode?.isDisabled || targetNode?.isDisabled) {
      link.opacity = LINKS_DIMMED_OPACITY;
      link.isDisabled = true;
    } else {
      link.opacity = LINKS_DEFAULT_OPACITY;
      link.isDisabled = false;
    }
  });

  // for root + 3 and deeper level nodes, only expand the first node with children
  let currentDepth = 3;
  let nodesAtDepth = nodes.filter(
    (node) =>
      node.isVisible && getNodeDepth(node, rootNodeKey, nodes) === currentDepth
  );

  while (nodesAtDepth.length > 0) {
    // find the first node at this depth that has visible children
    const nodeWithVisibleChildren = nodesAtDepth.find((node) =>
      nodes.some((n) => n.parentKey === node.key && n.isVisible)
    );

    if (!nodeWithVisibleChildren) break;

    // keep only this node expanded, collapse all other siblings
    collapseSiblings(
      nodeWithVisibleChildren.key,
      { nodes, links },
      rootNodeKey
    );
    currentDepth++; // move to the next depth level
    // get visible nodes at the next depth level
    nodesAtDepth = nodes.filter(
      (node) =>
        node.isVisible &&
        getNodeDepth(node, rootNodeKey, nodes) === currentDepth
    );
  }
}

// Check if node supports a specific view
export function nodeSupportsView(
  node: SankeyDataNode,
  viewName: string
): boolean {
  if (!node.views || !Array.isArray(node.views)) {
    return false;
  }
  // map Replay Tests to Quality view
  const targetViewName = viewName === 'Replay Tests' ? 'Quality' : viewName;

  return node.views.some((viewObj: any) => {
    return (
      Object.keys(viewObj).includes(targetViewName) &&
      Array.isArray(viewObj[targetViewName])
    );
  });
}

function expandDefaultLevel(
  rootNodeKey: string,
  nodes: SankeyDataNode[],
  links: SankeyDataLink[],
  selectedView?: string
) {
  let childNodes = nodes.filter((node) => node.parentKey === rootNodeKey);

  // filter children by view, including nodes whose descendants supports the view
  if (selectedView) {
    childNodes = childNodes.filter((node) => {
      const supportsView =
        nodeSupportsView(node, selectedView) ||
        nodes.some(
          (n) =>
            n.key.startsWith(node.key + '.') &&
            nodeSupportsView(n, selectedView)
        );

      // on toggle, for Quality view, show all nodes, but dim unsupported nodes without quality relevant descendants
      if (
        !supportsView &&
        (selectedView === 'Quality' || selectedView === 'Replay Tests')
      ) {
        node.opacity = NODES_DIMMED_OPACITY;
        node.isDisabled = true;
        return true;
      }

      return supportsView;
    });
  }

  // Separate "Other" node (for dynamic children) from individual children
  const otherNode = childNodes.find((node) => node.name === 'Other');
  const individualChildren = childNodes.filter((node) => node.name !== 'Other');

  // Apply limit to individual children only (Quality view children are a
  // fixed set of eval labels - never hide any)
  const nodesToShow =
    individualChildren.length > MAX_CHILD_ALLOWED &&
    selectedView !== 'Quality' &&
    selectedView !== 'Replay Tests'
      ? individualChildren.slice(0, MAX_CHILD_ALLOWED)
      : individualChildren;

  // Always include "Other" node if it exists
  if (otherNode) {
    nodesToShow.push(otherNode);
  }

  nodesToShow.forEach((node) => {
    node.isVisible = true;
  });

  links.forEach((link) => {
    const sourceNode = nodes.find((node) => node.key === link.source.key);
    const targetNode = nodes.find((node) => node.key === link.target.key);
    if (
      sourceNode?.isVisible &&
      targetNode?.isVisible &&
      sourceNode.key === rootNodeKey
    ) {
      link.isVisible = true;
    }
  });
}

// collapse all other sibling nodes at the same depth as the expanding node
export function collapseSiblings(
  expandingNodeKey: string,
  sankeyData: SankeyData,
  rootNodeKey: string
): void {
  const { nodes, links } = sankeyData;

  // find the node that is being expanded
  const expandingNode = nodes.find((node) => node.key === expandingNodeKey);
  if (!expandingNode) return;

  const targetDepth = getNodeDepth(expandingNode, rootNodeKey, nodes);

  // find all other nodes at the same depth as the expanding node
  const siblingNodes = nodes.filter(
    (node) =>
      getNodeDepth(node, rootNodeKey, nodes) === targetDepth &&
      node.key !== expandingNodeKey
  );

  // hide all descendants of this sibling node
  siblingNodes.forEach((sibling) => {
    nodes.forEach((node) => {
      if (node.key.startsWith(sibling.key + '.')) node.isVisible = false;
    });

    // hide all links originating from this sibling or its descendants
    links.forEach((link) => {
      const sourceKey = link.source.key;
      if (sourceKey === sibling.key || sourceKey.startsWith(sibling.key + '.'))
        link.isVisible = false;
    });
  });
}

/**
 * Collapse a node in the Sankey diagram, making its children invisible, and deleting
 * the "Other" node and its link if it exists.
 *
 * @param {string} nodeName The name of the node to collapse.
 * @param {Dispatch<SetStateAction<Set<string>>>} setRenderedLinksCache A dispatch
 *     function to update the rendered links cache.
 * @param {SankeyData} sankeyData The current Sankey data.
 * @returns {SankeyData} The updated Sankey data with the collapsed node and its
 *     children.
 */
export function collapseNode(
  nodeKey: string,
  setRenderedLinksCache: React.Dispatch<React.SetStateAction<Set<string>>>,
  sankeyData: SankeyData,
  renderedLinksCache: Set<string>,
  selectedView?: string
): SankeyData {
  const { nodes, links } = sankeyData;

  // Mark children of root as visible false
  const childNodes = getAllChildNodesInHierarchy(nodeKey, nodes);

  childNodes.forEach((node) => {
    node.isVisible = false;
  });

  // Mark links originating from this node as false.
  const updatedRenderedLinksCache = new Set(renderedLinksCache);
  links.forEach((link) => {
    const sourceNode = nodes.find((node) => node.key === link.source.key);
    const targetNode = nodes.find((node) => node.key === link.target.key);
    if (
      childNodes.some((node) => node.key === sourceNode?.key) ||
      sourceNode?.key === nodeKey
    ) {
      link.isVisible = false;
      const linkKey = `${sourceNode?.key}--${targetNode?.key}`;
      updatedRenderedLinksCache.delete(linkKey);
    }
  });
  setRenderedLinksCache(updatedRenderedLinksCache);

  return filterVisibleNodesAndLinks(nodes, links, selectedView);
}

/**
 * Recursively finds all child nodes of a given node in a hierarchical Sankey tree.
 *
 * @param {string} nodeName - The name of the node from which to find children.
 * @param {SankeyDataNode[]} nodes - The array of nodes representing the tree structure.
 * @returns {SankeyDataNode[]} - An array of all child nodes of the specified node.
 */
function getAllChildNodesInHierarchy(
  nodeKey: string,
  nodes: SankeyDataNode[]
): SankeyDataNode[] {
  const childNodes: SankeyDataNode[] = [];
  const childNodesOfNode = nodes.filter(
    (node) => node.parentKey === nodeKey && node.isVisible
  );
  childNodesOfNode.forEach((node) => {
    childNodes.push(node);
    childNodes.push(...getAllChildNodesInHierarchy(node.key, nodes));
  });
  return childNodes;
}
// Add "Other" nodes to group dynamic children (with '@' in key) beyond MAX_CHILD_ALLOWED
function addOtherNodes(sankeyData: SankeyData): SankeyData {
  const nodes = sankeyData.nodes;
  const links = sankeyData.links;

  nodes.forEach((parentNode) => {
    // Find children of this node
    const children = nodes.filter((node) => node.parentKey === parentNode.key);

    // Skip if there are no children
    if (children.length === 0) {
      return;
    }

    // Only apply "Other" grouping to dynamic nodes (identified by '@' in their key).
    const hasDynamicChildren = children.filter((child) =>
      child.key.includes('@')
    );
    if (hasDynamicChildren.length === 0) {
      return; // Skip if there's no dynamic children to group
    }
    const visibleChildren = hasDynamicChildren.slice(0, MAX_CHILD_ALLOWED);

    let childrenTotal = 0;
    visibleChildren.forEach((child) => {
      childrenTotal = childrenTotal + (child.value ?? 0);
    });

    // Calculate remaining value
    const parentValue = parentNode.value ?? 0;
    const remainingValue = parentValue - childrenTotal;

    // Create and add "Other" node with its link if needed
    if (remainingValue > 0) {
      const otherNode = {
        key: parentNode.key + '.Other',
        name: 'Other',
        value: remainingValue,
        parentKey: parentNode.key,
        isVisible: false,
        opacity: NODES_DEFAULT_OPACITY,
        views: hasDynamicChildren[0]?.views ?? parentNode.views,
      };

      nodes.push(otherNode);

      links.push({
        source: parentNode,
        target: otherNode,
        value: remainingValue,
        isVisible: false,
        opacity: LINKS_DEFAULT_OPACITY,
      });
    }
  });

  return { nodes: nodes, links: links };
}

/**
 * Recursively traverses a nested object and extracts a Sankey diagram structure
 * from it.
 *
 * @param {any} data - The nested object to extract the Sankey data from.
 * @returns {SankeyData} The Sankey data structure extracted from the object.
 */
export function extractSankeyData(data: any): SankeyData {
  const nodesMap = new Map<string, SankeyDataNode>(); // Map for unique nodes
  const links: SankeyDataLink[] = [];

  function traverse(obj: any, parentKey: string | null = null) {
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const keyParts = key.split('.');
        //if keyParts length is > 1, parentPart will be from 0 to len-1 and childPart will be entire key.
        const parentPart =
          keyParts.length > 1
            ? keyParts.slice(0, keyParts.length - 1).join('.')
            : parentKey;
        const childPart = key;
        const childName = obj[key].label;

        const views = obj[key].views;

        if (!nodesMap.has(childPart)) {
          nodesMap.set(childPart, {
            key,
            name: childName,
            value: obj[key].value,
            parentKey: parentPart,
            isVisible: false,
            opacity: NODES_DEFAULT_OPACITY,
            views: views,
          });
        }

        // Create a link if there's a valid parent and it's not a duplicate
        if (
          parentPart &&
          parentPart !== childPart &&
          nodesMap.has(childPart) &&
          nodesMap.has(parentPart)
        ) {
          const sourceNode = nodesMap.get(parentPart);
          const targetNode = nodesMap.get(childPart);

          // Although nodesMap.has() was checked, this explicit check helps TypeScript narrow down the types of sourceNode and targetNode to SankeyDataNode.
          // Given the preceding .has checks, these conditions will always be true.
          if (sourceNode && targetNode) {
            links.push({
              source: sourceNode,
              target: targetNode,
              value: obj[key].value,
              isVisible: false,
              opacity: LINKS_DEFAULT_OPACITY,
            });
          }
        }
      }
    }
  }

  traverse(data);

  // Convert nodesMap to an array of objects
  const nodes = Array.from(nodesMap.values());

  return addOtherNodes({ nodes, links });
}

// this function highlights the path from target node to root node, by reducing the opacity of other paths.
export function handlePathHighlightOnNodeSelection(
  targetNode: SankeyDataNode,
  dataSet: SankeyData,
  rootNodeKey: string
): SankeyData {
  const { nodes, links } = dataSet;
  // we need to find the entire hierarchy of parent nodes starting from target to root.
  // we can break them from targetNode.key as well also we can recursively find the parents as well.
  const nodesToBeHighlighted = [targetNode];
  let parentKey = targetNode.parentKey;
  while (parentKey) {
    const parentNode = nodes.find((node) => node.key === parentKey);
    if (parentNode) {
      nodesToBeHighlighted.push(parentNode);
      parentKey = parentNode.parentKey;
    }
  }
  const nodesToBeDimmed = nodes.filter(
    (node) => !nodesToBeHighlighted.includes(node)
  );

  // we dim a link if there's a dimmed node which can be a target node for this link.
  const linksToBeDimmed = links.filter((link) =>
    nodesToBeDimmed.some((node) => node.key === link.target.key)
  ) as PathLink[];

  // we highlight a link if there's a highlighted node which can be a source and another node which can be target node for this link
  const linksToBeHighlighted = links.filter(
    (link) =>
      nodesToBeHighlighted.some((node) => node.key === link.source.key) &&
      nodesToBeHighlighted.some((node) => node.key === link.target.key)
  ) as PathLink[];

  linksToBeHighlighted.forEach((link) => {
    link.opacity = LINKS_HIGHLIGHTED_OPACITY;
  });

  linksToBeDimmed.forEach((link) => {
    link.opacity =
      targetNode.key === rootNodeKey
        ? LINKS_DEFAULT_OPACITY
        : LINKS_DIMMED_OPACITY;
  });

  nodesToBeHighlighted.forEach((node) => {
    node.opacity = NODES_HIGHLIGHTED_OPACITY;
  });

  nodesToBeDimmed.forEach((node) => {
    node.opacity =
      targetNode.key === rootNodeKey
        ? NODES_DEFAULT_OPACITY
        : NODES_DIMMED_OPACITY;
  });

  return {
    nodes: [...nodesToBeHighlighted, ...nodesToBeDimmed],
    links: [...linksToBeHighlighted, ...linksToBeDimmed] as SankeyDataLink[],
  };
}

export function getPlaceholderTextFromNodeKey(
  selectedNode: RectNode | null,
  sankeyData: SankeyData | undefined
) {
  if (!selectedNode) return '';
  if (!sankeyData) {
    return selectedNode?.name;
  }
  // split the key by .
  const parts = selectedNode?.key.split('.');
  // for every part, get its name from sankeyData nodes, including all prefixes
  const names = parts.map((_, index) => {
    const prefix = parts.slice(0, index + 1).join('.');
    const node = sankeyData.nodes.find((node) => node.key === prefix);
    return node ? node.name : '';
  });
  return names.join(' > ');
}
